import { useIsMounted } from '@toss/react';
import { type SupportedLocale } from '@zep-us/i18n';
import { useTranslation } from 'next-i18next';
import { useViewport } from '@/hooks';
import { type ButtonProps, confirmDialog } from '@/ui_v3';
import { useSingleSelect } from '@/ui_v3/components/select/SingleSelect';
import { useLanguageChange } from './hooks/useLanguageChange';
import { useLanguageOptions } from './hooks/useLanguageOptions';
import { usePrefetchJaFonts } from './hooks/usePrefetchJaFonts';
import { LanguageSelectorContent } from './LanguageSelectorContent';
import { LanguageSelectorTrigger } from './LanguageSelectorTrigger';
export type LanguageSelectorProps = Pick<ButtonProps, 'rounded' | 'className' | 'size' | 'variant' | 'intent'> & {
  trigger?: React.ReactNode;
  iconSize?: '2xs' | 'xs' | 'sm' | 'md' | 'lg';
  hasConfirm?: boolean;
  eventProps: {
    location: string;
  };
  onChangeLanguageCallback?: (language: SupportedLocale) => void;
};
export function LanguageSelector(props: LanguageSelectorProps) {
  const {
    className,
    hasConfirm = false
  } = props;
  const isMounted = useIsMounted();
  const {
    isMobile
  } = useViewport();
  const {
    currentLocale,
    handleLanguageChange
  } = useLanguageChange(props.eventProps);
  const options = useLanguageOptions();
  const {
    isOpen,
    open,
    close,
    handleSelect: originalHandleSelect
  } = useSingleSelect({
    options,
    value: currentLocale,
    onValueChange: loc => {
      if (loc) {
        void handleLanguageChange(loc, () => {
          props.onChangeLanguageCallback?.(loc);
        });
      }
    }
  });
  const {
    t
  } = useTranslation(['common']);

  // 셀렉터가 열리면(언어 변경 의도) ja 폰트를 미리 받아 전환 직후 스왑 방지
  usePrefetchJaFonts(isOpen);
  const handleSelect = async (value: SupportedLocale) => {
    let confirm = true;
    if (hasConfirm) {
      confirm = await confirmDialog.openAsync({
        shouldCloseOnDimClick: false,
        header: {
          title: t('common.languageSelector.confirm')
        }
      });
    }
    if (confirm) {
      originalHandleSelect(value);
    }
  };
  if (!isMounted) {
    return null;
  }
  const variant = isMobile ? 'drawer' : 'select';
  const hasCustomTrigger = Boolean(props.trigger);
  const trigger = <LanguageSelectorTrigger {...props} hideTooltip={isOpen || isMobile} onClick={open} currentLocale={currentLocale} className={className} />;
  return <LanguageSelectorContent variant={variant} options={options} currentLocale={currentLocale} isOpen={isOpen} open={open} close={close} handleSelect={handleSelect} trigger={hasCustomTrigger ? props.trigger : trigger} data-sentry-element="LanguageSelectorContent" data-sentry-component="LanguageSelector" data-sentry-source-file="LanguageSelector.tsx" />;
}